import type { Skill } from '../content/types.ts';
import type { Roller } from './dice.ts';

/**
 * ORDINARY SKILL ADVANCEMENT.
 *
 * This is a different mechanic from the Tutorial-Floor untrained Skill award in
 * rules/sessionAdvancement.ts. That one grants a free Rank in a Skill the
 * crawler does not have; this one improves Skills through use. Nothing should
 * be shared between the two beyond the Skill list itself.
 *
 * RULE DCC-ADV-SKILL-001 - C&C p.42.
 * Whenever a Skill Check is attempted, Succeed or Fail, mark the Skill if it is
 * not already marked.
 *
 * RULE DCC-ADV-SKILL-002 - C&C p.42.
 * A Skill Advancement Check is 1d20 against the Skill's CURRENT Rank as the
 * Difficulty. Equal to or higher than that Rank gains 1 Rank.
 *
 * RULE DCC-ADV-SKILL-003 - C&C p.42.
 * Marked Skills at Rank 4 or lower are checked at the end of every 2 hours of
 * play. Marked Skills at Rank 5 or higher are checked at the end of each floor.
 *
 * RULE DCC-ADV-SKILL-004 - C&C p.42.
 * Ordinary Skill Rank is capped at 15; an explicit Race/Class exception permits 20.
 *
 * RULE DCC-ADV-SKILL-005 - C&C p.42.
 * Passive Skills do not receive advancement marks from use.
 */

/** Ordinary Rank ceiling. The legacy export name is retained for callers. */
export const MAX_RANK_FLOORS_1_TO_5 = 15;

/** The Rank at which a Skill moves from the 2-hour cadence to the floor cadence. */
export const FLOOR_CADENCE_MINIMUM_RANK = 5;

export type AdvancementCheckpoint = 'two-hour' | 'end-of-floor';

/**
 * RULE DCC-ADV-SKILL-005 - which Skills a Skill Check marks.
 *
 * C&C p.42 says "MOST of them" - Passive Skills - do not receive advancement
 * marks when used, and that Passive Skills are otherwise raised by magical
 * means, training or Race/Class benefits. It does not say all of them, and the
 * Skill entries carry the exceptions.
 *
 * Eligibility is therefore read from structured content, never from the Skill's
 * name or kind. Aiming (C&C p.27) is Passive but its entry says to mark it when
 * its Ranks are applied to a Ranged attack, so it is 'explicit-trigger'.
 */
export function canBeMarkedByUse(skill: Skill): boolean {
  return skill.advancement === 'normal-check-use';
}

/**
 * A Skill that advances only when its own entry's trigger fires, such as Aiming
 * being applied to a Ranged attack. The application marks these when the caller
 * reports the trigger, not from an ordinary Skill Check.
 */
export function canBeMarkedByExplicitTrigger(skill: Skill): boolean {
  return skill.advancement === 'explicit-trigger';
}

/**
 * A Damage Effect. Eligible for advancement, but C&C p.42 requires the player
 * to choose between the Attack Skill and the Damage Effect. That choice has no
 * trigger until automatic Damage Effect application exists, so nothing is
 * marked automatically. See docs/RULES_MATRIX.md, DCC-ADV-SKILL-006.
 */
export function requiresHandToHandChoice(skill: Skill): boolean {
  return skill.advancement === 'hand-to-hand-choice';
}

/** Which checkpoint resolves a marked Skill at this Rank. */
export function checkpointFor(rank: number): AdvancementCheckpoint {
  return rank >= FLOOR_CADENCE_MINIMUM_RANK ? 'end-of-floor' : 'two-hour';
}

/** Adds a mark if the Skill is not already marked. Never duplicates. */
export function addAdvancementMark(marks: readonly string[], skillId: string): string[] {
  return marks.includes(skillId) ? [...marks] : [...marks, skillId];
}

export function clearMarks(marks: readonly string[], resolved: readonly string[]): string[] {
  const done = new Set(resolved);
  return marks.filter((m) => !done.has(m));
}

export interface AdvancementRoll {
  skillId: string;
  /** Rank before the roll, which is also the Difficulty. */
  rankBefore: number;
  die: number;
  success: boolean;
  rankAfter: number;
  /** True when the Skill was already at the Rank cap and could not improve. */
  atCap: boolean;
}

/**
 * RULE DCC-ADV-SKILL-002 / 004 - one Skill Advancement Check.
 * The die is compared against the Skill's current Rank; meeting it counts.
 */
export function rollAdvancementCheck(
  roller: Roller,
  skillId: string,
  rank: number,
  maxRank = MAX_RANK_FLOORS_1_TO_5,
): AdvancementRoll {
  const atCap = rank >= maxRank;
  const die = atCap ? 0 : roller.roll(20);
  const success = die >= rank && !atCap;
  return {
    skillId,
    rankBefore: rank,
    die,
    success,
    rankAfter: success ? rank + 1 : rank,
    atCap,
  };
}

export interface SkillRankEntry {
  skillId: string;
  rank: number;
}

export interface CheckpointInput {
  skills: readonly SkillRankEntry[];
  marks: readonly string[];
  checkpoint: AdvancementCheckpoint;
  maxRankFor?: (skillId: string) => number;
  canResolveSkill?: (skillId: string) => boolean;
}

export interface CheckpointResult {
  rolls: AdvancementRoll[];
  skills: SkillRankEntry[];
  /** Marks left over: those belonging to the OTHER cadence. */
  remainingMarks: string[];
}

/**
 * Resolves every marked Skill that belongs to this checkpoint.
 *
 * Marks for the other cadence are deliberately left in place. C&C p.42 says to
 * "erase all marks" after resolving, but it describes the two cadences as
 * separate intervals; erasing a Rank 5+ mark at a 2-hour checkpoint would
 * silently deny that Skill the floor-end check the same page grants it. Only
 * marks actually resolved are erased. See docs/RULES_CONFLICTS.md
 * RESOLVED-005.
 */
export function processCheckpoint(roller: Roller, input: CheckpointInput): CheckpointResult {
  const byId = new Map(input.skills.map((s) => [s.skillId, s.rank]));
  const rolls: AdvancementRoll[] = [];
  const resolved: string[] = [];

  for (const skillId of new Set(input.marks)) {
    const rank = byId.get(skillId);
    // A mark for a Skill the crawler no longer has is dropped, not rolled.
    if (rank === undefined) {
      resolved.push(skillId);
      continue;
    }
    if (checkpointFor(rank) !== input.checkpoint || input.canResolveSkill?.(skillId) === false) continue;

    const roll = rollAdvancementCheck(roller, skillId, rank, input.maxRankFor?.(skillId));
    rolls.push(roll);
    byId.set(skillId, roll.rankAfter);
    resolved.push(skillId);
  }

  return {
    rolls,
    skills: input.skills.map((s) => ({ skillId: s.skillId, rank: byId.get(s.skillId) ?? s.rank })),
    remainingMarks: clearMarks(input.marks, resolved),
  };
}

/**
 * RULE DCC-ADV-LEVEL-001 - C&C p.43.
 * At the end of every 2 hours of play, each party member gains 1 level.
 * Quest, Boss, crawler-kill and grinding level gains are NOT implemented.
 */
export const LEVELS_PER_TWO_HOUR_CHECKPOINT = 1;
